<template>
  <div class="searchable-select" v-click-outside="closeDropdown">
    <!-- Trigger Button -->
    <div class="select-trigger" @click="toggleDropdown" :class="{ 'is-open': isOpen, 'is-disabled': disabled }">
      <span v-if="selectedLabel" class="selected-text">{{ selectedLabel }}</span>
      <span v-else class="placeholder-text">{{ placeholder }}</span>
      
      <div class="trigger-actions">
        <button v-if="modelValue && !disabled" type="button" class="clear-btn" @click.stop="clearSelection">
          &times;
        </button>
        <span class="dropdown-arrow"></span>
      </div>
    </div>

    <!-- Dropdown Menu -->
    <div v-if="isOpen" class="select-dropdown">
      <!-- Search Input -->
      <div v-if="searchable" class="search-box-wrapper" @click.stop>
        <input
          ref="searchInput"
          type="text"
          v-model="searchQuery"
          placeholder="Qidirish..."
          class="dropdown-search-input"
        />
      </div>

      <!-- Options List -->
      <ul class="options-list">
        <li
          v-if="filteredOptions.length === 0"
          class="no-options"
        >
          Ma'lumot topilmadi
        </li>
        <li
          v-for="(opt, idx) in filteredOptions"
          :key="getOptionKey(opt, idx)"
          @click="selectOption(opt)"
          :class="['option-item', { 'is-selected': isSelected(opt), 'is-already-added': opt && opt.alreadyAdded }]"
        >
          {{ getOptionLabel(opt) }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "SearchableSelect",
  props: {
    modelValue: {
      type: [String, Number, Object],
      default: ""
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: "Tanlang..."
    },
    itemValue: {
      type: String,
      default: ""
    },
    itemLabel: {
      type: String,
      default: ""
    },
    searchable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      isOpen: false,
      searchQuery: ""
    };
  },
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unmounted(el) {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      }
    }
  },
  computed: {
    selectedLabel() {
      if (!this.modelValue) return "";
      const matched = this.options.find(opt => {
        const val = this.getOptionValue(opt);
        return String(val) === String(this.modelValue);
      });
      return matched ? this.getOptionLabel(matched) : String(this.modelValue);
    },
    filteredOptions() {
      if (!this.searchQuery.trim()) return this.options;
      
      const normalize = (str) => {
        return (str || "")
          .toString()
          .toLowerCase()
          .replace(/[‘'’`ʻʼ\u02BB\u02BC\u2018\u2019\u201B\u0027]/g, "'")
          .replace(/[\u0430\u0410]/g, 'a')
          .replace(/[\u0435\u0415]/g, 'e')
          .replace(/[\u043E\u041E]/g, 'o')
          .replace(/[\u0441\u0421]/g, 'c')
          .replace(/[\u0440\u0420]/g, 'r')
          .replace(/[\u0445\u0425]/g, 'x')
          .replace(/degred/g, 'degrad')
          .trim();
      };

      const cleanAlphanum = (str) => normalize(str).replace(/[^a-z0-9]/g, '');

      const rawQuery = this.searchQuery.trim();
      const normQuery = normalize(rawQuery);
      const cleanQuery = cleanAlphanum(rawQuery);
      const queryTokens = normQuery.split(/[\s,_\-()]+/).filter(Boolean);

      return this.options.filter(opt => {
        const label = this.getOptionLabel(opt);
        const val = this.getOptionValue(opt);
        const rawName = opt && opt.rawName ? String(opt.rawName) : "";
        const shortName = opt && opt.shortName ? String(opt.shortName) : "";
        const currName = opt && opt.curriculumName ? String(opt.curriculumName) : "";
        const searchTerms = opt && opt.searchTerms ? String(opt.searchTerms) : "";
        const specialty = opt && opt.specialtyName ? String(opt.specialtyName) : "";

        const allText = `${label} ${val} ${rawName} ${shortName} ${currName} ${searchTerms} ${specialty}`;
        const normTarget = normalize(allText);
        const cleanTarget = cleanAlphanum(allText);

        // 1. Direct substring match
        if (normTarget.includes(normQuery)) return true;

        // 2. Alphanumeric match (ignores spaces, underscores, hyphens, brackets)
        if (cleanQuery && cleanTarget.includes(cleanQuery)) return true;

        // 3. Multi-word match: every word in query must appear in target
        if (queryTokens.length > 0) {
          const allTokensMatch = queryTokens.every(tok => {
            const cleanTok = cleanAlphanum(tok);
            return normTarget.includes(tok) || (cleanTok && cleanTarget.includes(cleanTok));
          });
          if (allTokensMatch) return true;
        }

        return false;
      });
    }
  },
  methods: {
    getOptionKey(opt, idx) {
      const v = this.getOptionValue(opt);
      if (v) return `${v}_${idx}`;
      return `opt_${idx}`;
    },
    getOptionValue(opt) {
      if (opt === null || opt === undefined) return "";
      if (typeof opt === "object") {
        let val = this.itemValue ? opt[this.itemValue] : (opt.id !== undefined ? opt.id : (opt.code !== undefined ? opt.code : opt.value));
        if (val && typeof val === "object") {
          val = val.id || val._id || val.code || val.name || "";
        }
        if (val !== null && val !== undefined && typeof val !== "object") {
          return String(val);
        }
        return opt.name || opt.label || "";
      }
      return String(opt);
    },
    getOptionLabel(opt) {
      if (opt === null || opt === undefined) return "";
      if (typeof opt === "object") {
        let lbl = this.itemLabel ? opt[this.itemLabel] : (opt.name || opt.label || opt.title);
        if (lbl && typeof lbl === "object") {
          lbl = lbl.name || lbl.title || lbl.label || "";
        }
        if (lbl !== null && lbl !== undefined && typeof lbl !== "object") {
          return String(lbl);
        }
        return "";
      }
      return String(opt);
    },
    toggleDropdown() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.searchQuery = "";
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    closeDropdown() {
      this.isOpen = false;
    },
    selectOption(opt) {
      const val = this.getOptionValue(opt);
      this.isOpen = false;
      this.$emit("update:modelValue", val);
      this.$emit("change", val);
    },
    clearSelection() {
      this.$emit("update:modelValue", "");
      this.$emit("change", "");
      this.isOpen = false;
    },
    isSelected(opt) {
      const v = this.getOptionValue(opt);
      const m = this.modelValue !== null && this.modelValue !== undefined ? String(this.modelValue) : "";
      if (v === "" && m === "") return false;
      return v === m;
    }
  }
};
</script>

<style scoped>
.searchable-select {
  position: relative;
  width: 100%;
  font-family: inherit;
  user-select: none;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 40px;
}

.select-trigger:hover {
  border-color: #cbd5e1;
}

.select-trigger.is-open {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.select-trigger.is-disabled {
  opacity: 0.75;
  cursor: not-allowed;
  background-color: #f8fafc;
  border-color: #e2e8f0;
}

.selected-text {
  font-size: 13px;
  color: #334155;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 8px;
}

.placeholder-text {
  font-size: 13px;
  color: #94a3b8;
}

.trigger-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-btn {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 16px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.clear-btn:hover {
  color: #64748b;
}

.dropdown-arrow {
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid #64748b;
  transition: transform 0.2s;
}

.select-trigger.is-open .dropdown-arrow {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 100;
  overflow: hidden;
}

.search-box-wrapper {
  padding: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.dropdown-search-input {
  width: 100%;
  padding: 8px 12px;
  font-size: 13px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  outline: none;
}

.dropdown-search-input:focus {
  border-color: #3b82f6;
}

.options-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 220px;
  overflow-y: auto;
}

.option-item {
  padding: 10px 16px;
  font-size: 13px;
  color: #334155;
  cursor: pointer;
  transition: background-color 0.15s;
  word-break: break-word;
  line-height: 1.4;
}

.option-item:hover {
  background-color: #f1f5f9;
}

.option-item.is-selected {
  background-color: #0d6efd;
  color: #ffffff;
}

.option-item.is-already-added {
  background-color: #f8fafc;
  color: #94a3b8;
  cursor: default;
}

.option-item.is-already-added:hover {
  background-color: #f1f5f9;
}

.no-options {
  padding: 12px 16px;
  font-size: 13px;
  color: #94a3b8;
  text-align: center;
}
</style>
